import React, { useCallback } from 'react';
import { Internal } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { Box, Button } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useAuthContext } from '../../context/AuthContextProvider';
import { APP_PATHS, PAGE_PATHS } from '../../types';
import { useNavigate } from '../../utils/AvRouter';
import { getPathNameByAccount } from '../../utils/router.utils';

const SNACKBAR_KEY = 'connected-products-warning';

const ConnectedProductsWarningContent = ({ onResolve }: { onResolve: () => void }) => (
  <Box sx={{ ...flex.colItemsStart }}>
    <Box sx={{ fontWeight: 600 }}>Zscaler Connected Products requires further input and configuration</Box>
    <Box sx={{ fontWeight: 400 }}>Until resolved some functionality and apps may not work in the account</Box>
    <Button variant="primaryText" onClick={onResolve} sx={{ mt: 2 }}>
      Resolve
      <Internal style={{ width: 18, height: 18 }} />
    </Button>
  </Box>
);

export const useConnectedProductsWarning = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { accountId } = useAuthContext();

  const getPathName = getPathNameByAccount(accountId, APP_PATHS.SETTINGS);

  const close = useCallback(() => {
    closeSnackbar(SNACKBAR_KEY);
  }, [closeSnackbar]);

  const show = useCallback(() => {
    const { to } = getPathName(PAGE_PATHS.CONNECTED_PRODUCTS, '', APP_PATHS.SETTINGS);
    enqueueSnackbar(
      <ConnectedProductsWarningContent
        onResolve={() => {
          closeSnackbar(SNACKBAR_KEY);
          navigate({ to });
        }}
      />,
      {
        key: SNACKBAR_KEY,
        variant: 'warning',
        persist: true,
        preventDuplicate: true,
        sx: { minWidth: 566 },
      } as any
    );
  }, [closeSnackbar, enqueueSnackbar, navigate, getPathName]);

  const updateStatus = useCallback(
    (status?: string) => {
      if (status === 'PROVISIONED_WITH_ERRORS') {
        show();
      } else {
        close();
      }
    },
    [show, close]
  );

  return { show, close, updateStatus };
};
